/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * SubAgentEventsTree — renders nested sub-agent events under a parent
 * tool call. Shared across all AI chat surfaces so the rendering stays
 * consistent between the main assistant, the agent chat and the AI
 * Builder chat.
 *
 * Each event carries the envelope produced by `AgentTool` on the
 * backend (see `AI/agents/tools/agent_tool.py`):
 *     source, parent_tool_call_id, sub_agent_id, sub_agent_name,
 *     delegation_depth, type, ...
 *
 * Rendering decisions:
 *   - tool_call events are containers; they recursively render their
 *     own `subAgentEvents` list (grandchildren).
 *   - tool_result events merged into their matching tool_call are
 *     surfaced inside the nested tool card (via `event.result`).
 *   - llm_response events show the assistant text streamed by the
 *     sub-agent. We display the last-wins content because the backend
 *     emits full content per chunk.
 *   - execution_started / execution_completed / execution_error are
 *     light status rows.
 *
 * The component uses plain HTML + inline styles so it renders in both
 * the Tailwind-styled chat components and the MUI-based AI Builder.
 */

import React, { useState } from 'react';
import MarkdownRenderer from '@/components/MarkdownRenderer';

const ROW = {
  fontSize: '12px',
  padding: '3px 0',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '6px',
  lineHeight: 1.4,
};

// llm_response chunks from sub-agents are streamed; many arrive with
// empty/whitespace content while the LLM is still "thinking" between
// tool calls. Rendering each as a row creates large visual gaps and
// pushes the meaningful final response off-screen, so filter them out.
function hasVisibleContent(event) {
  if (event.type !== 'llm_response') return true;
  return typeof event.content === 'string' && event.content.trim().length > 0;
}

function isCancelledToolResult(result) {
  return !!(result && (result.cancelled === true || result.data?.cancelled === true));
}

function renderStatusLabel(event) {
  switch (event.type) {
    case 'execution_started':
      return (
        <span style={{ color: '#6b7280' }}>
          <span style={{ marginRight: 4 }}>🚀</span>
          <b>{event.sub_agent_name || 'sub-agent'}</b> started
        </span>
      );
    case 'execution_completed':
      return (
        <span style={{ color: event.status === 'COMPLETED' ? '#16a34a' : '#6b7280' }}>
          <span style={{ marginRight: 4 }}>{event.status === 'CANCELLED' ? '■' : '✅'}</span>
          <b>{event.sub_agent_name || 'sub-agent'}</b> {event.status === 'CANCELLED' ? 'stopped' : 'finished'}
          {event.status ? ` (${event.status})` : ''}
        </span>
      );
    case 'execution_error':
      return (
        <span style={{ color: '#dc2626' }}>
          <span style={{ marginRight: 4 }}>❌</span>
          <b>{event.sub_agent_name || 'sub-agent'}</b> error: {String(event.error || '')}
        </span>
      );
    case 'llm_response':
      // Render the sub-agent's final answer using the same markdown
      // pipeline as the parent assistant bubble so headings/lists/code
      // pick up theme colors instead of looking dim/grayed-out.
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 6,
            color: 'var(--color-text-primary, inherit)',
            fontSize: 13,
            lineHeight: 1.5,
            width: '100%',
          }}
        >
          <span style={{ marginTop: 1 }}>💬</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <MarkdownRenderer content={event.content || ''} />
          </div>
        </div>
      );
    default:
      return null;
  }
}

function NestedToolCall({ event, depth }) {
  const [expanded, setExpanded] = useState(false);
  const isRunning = event.result === undefined || event.result === null;
  const isCancelled = isCancelledToolResult(event.result);
  const isSuccess = event.success === true || (event.result && event.result.success === true);
  const statusColor = isRunning ? '#f59e0b' : isCancelled ? '#6b7280' : isSuccess ? '#16a34a' : '#dc2626';
  const statusText = isRunning ? 'Running...' : isCancelled ? 'Stopped' : isSuccess ? 'Completed' : 'Failed';
  const statusIcon = isRunning ? '⏳' : isCancelled ? '■' : isSuccess ? '✓' : '✗';

  return (
    <div
      style={{
        border: '1px solid rgba(99,102,241,0.25)',
        borderRadius: 6,
        background: 'rgba(99,102,241,0.05)',
        marginTop: 4,
      }}
    >
      <div
        onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 8px',
          fontSize: 12,
        }}
      >
        <span
          style={{
            fontSize: 10,
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s ease',
          }}
        >
          ▶
        </span>
        <span>🔧</span>
        <span style={{ flex: 1, fontWeight: 500 }}>{event.tool_name}</span>
        <span
          style={{
            fontSize: 10,
            padding: '1px 6px',
            borderRadius: 8,
            background: `color-mix(in srgb, ${statusColor} 15%, transparent)`,
            color: statusColor,
            fontWeight: 500,
          }}
        >
          {statusIcon} {statusText}
        </span>
      </div>
      {expanded && (
        <div style={{ padding: '4px 10px 8px 24px' }}>
          {event.tool_args && Object.keys(event.tool_args).length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Arguments
              </div>
              <pre
                style={{
                  margin: 0,
                  padding: 6,
                  borderRadius: 4,
                  background: 'rgba(0,0,0,0.06)',
                  fontSize: 11,
                  overflow: 'auto',
                  maxHeight: 120,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(event.tool_args, null, 2)}
              </pre>
            </div>
          )}
          {event.result && (
            <div>
              <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Result
              </div>
              <pre
                style={{
                  margin: 0,
                  padding: 6,
                  borderRadius: 4,
                  background: 'rgba(0,0,0,0.06)',
                  fontSize: 11,
                  overflow: 'auto',
                  maxHeight: 160,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(event.result, null, 2)}
              </pre>
            </div>
          )}
          {Array.isArray(event.subAgentEvents) && event.subAgentEvents.length > 0 && (
            <SubAgentEventsTree events={event.subAgentEvents} depth={(depth || 0) + 1} />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * @param {{ events: Array<any>, depth?: number, title?: string }} props
 */
export function SubAgentEventsTree({ events, depth = 0, title = 'Sub-agent activity' }) {
  if (!Array.isArray(events) || events.length === 0) return null;

  return (
    <div
      style={{
        marginTop: 8,
        paddingLeft: 10,
        borderLeft: '2px solid rgba(99,102,241,0.35)',
      }}
    >
      {depth === 0 && (
        <div
          style={{
            fontSize: 10,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            marginBottom: 4,
          }}
        >
          {title}
        </div>
      )}
      {events.filter(hasVisibleContent).map((event, idx) => {
        if (event.type === 'tool_call') {
          // Always suffix idx so duplicate tool_call_ids (which can happen
          // when the upstream provider omits ids and the backend's
          // per-iteration fallback collides) don't trigger React key
          // collision warnings and silent child drops.
          return (
            <NestedToolCall
              key={`${event.tool_call_id || 'tc'}-${idx}`}
              event={event}
              depth={depth}
            />
          );
        }
        const label = renderStatusLabel(event);
        if (!label) return null;
        return (
          <div key={`${event.type}-${idx}`} style={ROW}>
            {label}
          </div>
        );
      })}
    </div>
  );
}

export default SubAgentEventsTree;
